import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type CollisionDetection, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { coursePreviewPath } from './previewPath';
import { lessonDndId, parseDndId, resolveLessonDrop, sectionDndId } from './lessonDrop';
import Add from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicator from '@mui/icons-material/DragIndicator';
import DriveFileMoveOutlined from '@mui/icons-material/DriveFileMoveOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import MoreVert from '@mui/icons-material/MoreVert';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import { Title, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { courseStructureService } from '../../../services/courseStructureService';
import { BlockLessonEditor, type BlockLessonEditorLesson } from '../editors/BlockEditor/BlockLessonEditor';
import { CourseSourcesDialog } from './CourseSourcesDialog';
import { CourseVariantsDialog } from './CourseVariantsDialog';
import tokenService from '../../../services/tokenService';
import './course-builder.css';

interface Course {
    id: number;
    title: string;
    contentFormat?: 'classic' | 'blocks';
    isActive?: boolean;
    aiOrigin?: 'generated' | 'assisted' | 'variant' | null;
    signedOffAt?: string | null;
}

interface Section {
    id: number;
    title: string;
    description?: string | null;
    position: number;
}

interface Lesson extends BlockLessonEditorLesson {
    section?: string | number | { id: number | string } | null;
    sortOrder: number;
    estimatedDuration?: number | null;
    isMandatory?: boolean;
    isActive?: boolean;
}

// Tolerant gegen IRI-Strings und eingebettete Objekte mit String-ID — der
// dataProvider lieferte die Sektion zeitweise als { id: "9" } (Stage, 2026-10-14).
const refId = (ref: Lesson['section']): number | null => {
    if (ref == null) return null;
    if (typeof ref === 'number') return ref;
    if (typeof ref === 'object') {
        const n = Number(ref.id);
        return Number.isFinite(n) ? n : null;
    }
    const m = /(\d+)$/.exec(ref);
    return m ? Number(m[1]) : null;
};

/* ───────────────────────── sortable rows ───────────────────────── */

const SortableRow: React.FC<{ id: string; className: string; children: (handle: React.ReactNode) => React.ReactNode }> = ({ id, className, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const handle = (
        <span className="oa-cb-handle" {...attributes} {...listeners} aria-label="drag">
            <DragIndicator fontSize="small" />
        </span>
    );
    return (
        <div ref={setNodeRef} className={`${className}${isDragging ? ' is-dragging' : ''}`} style={{ transform: CSS.Transform.toString(transform), transition }}>
            {children(handle)}
        </div>
    );
};

/* ───────────────────────── page ───────────────────────── */

/**
 * Kurs-Builder (Autorentool v2, plan §6.6): sections + block lessons in the left
 * sidebar, the selected lesson's BlockLessonEditor on the right. Classic courses
 * are redirected to the classic lessons page and vice versa.
 */
const CourseBuilder: React.FC = () => {
    const { courseId, lessonId } = useParams<{ courseId: string; lessonId?: string }>();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    const [course, setCourse] = useState<Course | null>(null);
    const [sections, setSections] = useState<Section[]>([]);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [variantsOpen, setVariantsOpen] = useState(false);
    const hasVariantsFeature = tokenService.hasFeature('course.ai_variants');

    const [sectionDialog, setSectionDialog] = useState<{ open: boolean; section: Section | null; title: string }>({ open: false, section: null, title: '' });
    const [lessonDialog, setLessonDialog] = useState<{ open: boolean; lesson: Lesson | null; sectionId: number | null; title: string; duration: string; mandatory: boolean }>({
        open: false, lesson: null, sectionId: null, title: '', duration: '', mandatory: true,
    });
    const [menu, setMenu] = useState<{ anchor: HTMLElement; lesson: Lesson } | null>(null);
    const [moveMenu, setMoveMenu] = useState<{ anchor: HTMLElement; lesson: Lesson } | null>(null);

    const selectedId = lessonId ? Number(lessonId) : null;
    const selected = useMemo(() => lessons.find((l) => l.id === selectedId) ?? null, [lessons, selectedId]);

    const load = useCallback(async () => {
        if (!courseId) return;
        try {
            const [{ data: c }, { data: s }, { data: l }] = await Promise.all([
                dataProvider.getOne('courses', { id: Number(courseId) }),
                dataProvider.getList('course_sections', { pagination: { page: 1, perPage: 200 }, sort: { field: 'position', order: 'ASC' }, filter: { 'course.id': courseId } }),
                dataProvider.getList('lessons', { pagination: { page: 1, perPage: 500 }, sort: { field: 'sortOrder', order: 'ASC' }, filter: { 'course.id': courseId } }),
            ]);
            const courseData = c as Course;
            if (courseData.contentFormat !== 'blocks') {
                navigate(`/courses/${courseId}/lessons`, { replace: true });
                return;
            }
            setCourse(courseData);
            setSections((s as Section[]).slice().sort((a, b) => a.position - b.position || a.id - b.id));
            setLessons((l as Lesson[]).slice().sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id));
        } catch {
            notify('courseBuilder.loadError', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [courseId, dataProvider, navigate, notify]);

    useEffect(() => {
        void load();
    }, [load]);

    const lessonsOf = (sectionId: number) => lessons.filter((l) => refId(l.section) === sectionId);
    const orphans = lessons.filter((l) => refId(l.section) === null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

    const run = async (fn: () => Promise<unknown>, errorKey: string) => {
        setBusy(true);
        try {
            await fn();
            await load();
        } catch (e: unknown) {
            const detail = (e as { body?: { error?: string; detail?: string } })?.body;
            notify(detail?.error ?? detail?.detail ?? translate(errorKey), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    /* sections */
    /** One drag handler for sections and lessons (#526: lessons may cross sections). */
    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const activeRef = parseDndId(active.id);
        if (activeRef?.kind === 'section') {
            const overRef = parseDndId(over.id);
            const overSectionId = overRef?.kind === 'section' ? overRef.id : lessons.find((l) => l.id === overRef?.id)?.section ? refId(lessons.find((l) => l.id === overRef?.id)!.section) : null;
            if (overSectionId === null || overSectionId === activeRef.id) return;
            const ids = sections.map((s) => s.id);
            const reordered = arrayMove(ids, ids.indexOf(activeRef.id), ids.indexOf(overSectionId));
            setSections(reordered.map((id, i) => ({ ...sections.find((s) => s.id === id)!, position: i })));
            void run(() => courseStructureService.reorderSections(Number(courseId), reordered), 'courseBuilder.reorderError');
            return;
        }
        const drop = resolveLessonDrop(lessons.map((l) => ({ id: l.id, sectionId: refId(l.section) })), active.id, over.id);
        if (!drop) return;
        if (drop.type === 'reorder') {
            void run(() => courseStructureService.reorderLessons(drop.sectionId, drop.orderedIds), 'courseBuilder.reorderError');
        } else {
            void run(() => courseStructureService.moveLesson(drop.lessonId, drop.targetSectionId, drop.position), 'courseBuilder.reorderError');
        }
    };

    /** Lessons are dropped on lessons; a section is the target only when it has no lesson to aim at. */
    const collisionDetection: CollisionDetection = (args) => {
        const activeRef = parseDndId(args.active.id);
        if (activeRef?.kind !== 'lesson') {
            return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => parseDndId(c.id)?.kind === 'section') });
        }
        const hit = closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => parseDndId(c.id)?.kind === 'lesson') });
        const emptySections = args.droppableContainers.filter((c) => {
            const ref = parseDndId(c.id);
            return ref?.kind === 'section' && lessonsOf(ref.id).length === 0;
        });
        const emptyHit = emptySections.length ? closestCenter({ ...args, droppableContainers: emptySections }) : [];
        return [...hit.slice(0, 1), ...emptyHit.slice(0, 1)].sort((a, b) => Number(a.data?.value ?? 0) - Number(b.data?.value ?? 0));
    };

    const saveSection = () =>
        run(async () => {
            const title = sectionDialog.title.trim() || translate('courseBuilder.section.untitled');
            if (sectionDialog.section) {
                await dataProvider.update('course_sections', { id: sectionDialog.section.id, data: { title }, previousData: sectionDialog.section });
            } else {
                await dataProvider.create('course_sections', { data: { course: `/api/courses/${courseId}`, title } });
            }
            setSectionDialog({ open: false, section: null, title: '' });
        }, 'courseBuilder.section.saveError');

    const deleteSection = (section: Section) => {
        if (lessonsOf(section.id).length > 0) {
            notify('courseBuilder.section.notEmpty', { type: 'warning' });
            return;
        }
        if (!window.confirm(translate('courseBuilder.section.confirmDelete', { title: section.title }))) return;
        void run(() => dataProvider.delete('course_sections', { id: section.id, previousData: section }), 'courseBuilder.section.deleteError');
    };

    /* lessons */
    const saveLesson = () =>
        run(async () => {
            const title = lessonDialog.title.trim() || translate('courseBuilder.lesson.untitled');
            const duration = lessonDialog.duration === '' ? null : Number(lessonDialog.duration);
            if (lessonDialog.lesson) {
                await dataProvider.update('lessons', {
                    id: lessonDialog.lesson.id,
                    data: { title, estimatedDuration: duration, isMandatory: lessonDialog.mandatory },
                    previousData: lessonDialog.lesson,
                });
            } else {
                const { data } = await dataProvider.create('lessons', {
                    data: {
                        course: `/api/courses/${courseId}`,
                        section: `/api/course_sections/${lessonDialog.sectionId}`,
                        title,
                        type: 'blocks',
                        content: { schemaVersion: 1, completionRule: 'view_only', blocks: [] },
                        estimatedDuration: duration,
                        isMandatory: lessonDialog.mandatory,
                        isActive: true,
                        sortOrder: lessons.length,
                    },
                });
                navigate(`/courses/${courseId}/builder/${(data as Lesson).id}`);
            }
            setLessonDialog((d) => ({ ...d, open: false }));
        }, 'courseBuilder.lesson.saveError');

    const deleteLesson = (lesson: Lesson) => {
        if (!window.confirm(translate('lessons.confirmDelete'))) return;
        void run(async () => {
            await dataProvider.delete('lessons', { id: lesson.id, previousData: lesson });
            if (selectedId === lesson.id) navigate(`/courses/${courseId}/builder`, { replace: true });
        }, 'lessons.deleteError');
    };

    const moveLesson = (lesson: Lesson, target: Section) => {
        setMoveMenu(null);
        void run(() => courseStructureService.moveLesson(lesson.id, target.id, lessonsOf(target.id).length), 'courseBuilder.reorderError');
    };

    const onLessonSaved = useCallback((saved: BlockLessonEditorLesson) => {
        setLessons((prev) => prev.map((l) => (l.id === saved.id ? { ...l, content: saved.content, minScore: saved.minScore } : l)));
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }
    if (!course) return null;

    return (
        <Box className="oa-course-builder">
            <Title title={translate('courseBuilder.title', { title: course.title })} />
            <Box className="oa-cb-header">
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/courses/catalogs')}>{translate('lessons.backToCourses')}</Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>{course.title}</Typography>
                    <Chip size="small" label={translate('courseBuilder.badge')} />
                    {course.isActive === false && <Chip size="small" color="warning" label={translate('courseBuilder.inactive')} />}
                    {course.aiOrigin && <Chip size="small" icon={<SmartToyOutlined />} label={translate(`provenance.badge.${course.aiOrigin}`)} />}
                    {course.signedOffAt && <Chip size="small" color="success" variant="outlined" icon={<CheckCircleOutline />} label={translate('provenance.badge.signedOff')} />}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<LightbulbOutlined />} onClick={() => navigate(`/courses/${course.id}/quality`)}>
                    {translate('courseBuilder.quality')}
                </Button>
                {course.aiOrigin && (
                    <Button variant="outlined" startIcon={<MenuBookOutlined />} onClick={() => setSourcesOpen(true)}>
                        {translate('courseBuilder.sources')}
                    </Button>
                )}
                <CourseSourcesDialog courseId={Number(course.id)} open={sourcesOpen} onClose={() => setSourcesOpen(false)} />
                {hasVariantsFeature && (
                    <Button variant="outlined" startIcon={<TranslateOutlined />} onClick={() => setVariantsOpen(true)}>
                        {translate('variants.button')}
                    </Button>
                )}
                <CourseVariantsDialog courseId={Number(course.id)} open={variantsOpen} onClose={() => setVariantsOpen(false)} />
                <Button variant="outlined" startIcon={<PlayCircleOutline />} onClick={() => navigate(coursePreviewPath(course.id))}>
                    {translate('lessons.preview')}
                </Button>
            </Box>

            <Box className="oa-cb-layout">
                <aside className="oa-cb-sidebar" aria-label={translate('courseBuilder.structure')}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">{translate('courseBuilder.structure')}</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => setSectionDialog({ open: true, section: null, title: '' })} disabled={busy}>
                            {translate('courseBuilder.section.add')}
                        </Button>
                    </Box>

                    {sections.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{translate('courseBuilder.section.empty')}</Typography>
                    )}

                    <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={onDragEnd}>
                        <SortableContext items={sections.map((s) => sectionDndId(s.id))} strategy={verticalListSortingStrategy}>
                            {sections.map((section) => {
                                const sectionLessons = lessonsOf(section.id);
                                return (
                                    <SortableRow key={section.id} id={sectionDndId(section.id)} className="oa-cb-section">
                                        {(handle) => (
                                            <>
                                                <div className="oa-cb-section-head">
                                                    {handle}
                                                    <span className="oa-cb-section-title">{section.title}</span>
                                                    <span className="oa-cb-count">{sectionLessons.length}</span>
                                                    <Tooltip title={translate('courseBuilder.section.rename')}>
                                                        <IconButton size="small" onClick={() => setSectionDialog({ open: true, section, title: section.title })}><EditOutlined fontSize="inherit" /></IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={translate('ra.action.delete')}>
                                                        <IconButton size="small" onClick={() => deleteSection(section)}><DeleteOutline fontSize="inherit" /></IconButton>
                                                    </Tooltip>
                                                </div>
                                                    <SortableContext items={sectionLessons.map((l) => lessonDndId(l.id))} strategy={verticalListSortingStrategy}>
                                                        {sectionLessons.map((lesson) => (
                                                            <SortableRow key={lesson.id} id={lessonDndId(lesson.id)} className={`oa-cb-lesson${lesson.id === selectedId ? ' is-active' : ''}`}>
                                                                {(lessonHandle) => (
                                                                    <>
                                                                        {lessonHandle}
                                                                        <button type="button" className="oa-cb-lesson-btn" onClick={() => navigate(`/courses/${courseId}/builder/${lesson.id}`)}>
                                                                            <span className="oa-cb-lesson-title">{lesson.title}</span>
                                                                            <span className="oa-cb-lesson-meta">
                                                                                {translate('courseBuilder.lesson.blocks', { smart_count: lesson.content?.blocks?.length ?? 0 })}
                                                                                {lesson.estimatedDuration ? ` · ${lesson.estimatedDuration} min` : ''}
                                                                            </span>
                                                                        </button>
                                                                        <IconButton size="small" aria-label={translate('courseBuilder.lesson.menu')} onClick={(e) => setMenu({ anchor: e.currentTarget, lesson })}>
                                                                            <MoreVert fontSize="inherit" />
                                                                        </IconButton>
                                                                    </>
                                                                )}
                                                            </SortableRow>
                                                        ))}
                                                    </SortableContext>
                                                <Button size="small" startIcon={<Add />} className="oa-cb-add-lesson" disabled={busy} onClick={() => setLessonDialog({ open: true, lesson: null, sectionId: section.id, title: '', duration: '', mandatory: true })}>
                                                    {translate('courseBuilder.lesson.add')}
                                                </Button>
                                            </>
                                        )}
                                    </SortableRow>
                                );
                            })}
                        </SortableContext>
                    </DndContext>

                    {orphans.length > 0 && (
                        <Box className="oa-cb-section">
                            <div className="oa-cb-section-head"><span className="oa-cb-section-title">{translate('courseBuilder.section.unassigned')}</span></div>
                            {orphans.map((lesson) => (
                                <div key={lesson.id} className={`oa-cb-lesson${lesson.id === selectedId ? ' is-active' : ''}`}>
                                    <button type="button" className="oa-cb-lesson-btn" onClick={() => navigate(`/courses/${courseId}/builder/${lesson.id}`)}>
                                        <span className="oa-cb-lesson-title">{lesson.title}</span>
                                    </button>
                                    <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, lesson })}><MoreVert fontSize="inherit" /></IconButton>
                                </div>
                            ))}
                        </Box>
                    )}
                </aside>

                <main className="oa-cb-main">
                    {selected ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>{selected.title}</Typography>
                                <IconButton size="small" aria-label={translate('courseBuilder.lesson.edit')} onClick={() => setLessonDialog({ open: true, lesson: selected, sectionId: refId(selected.section), title: selected.title, duration: selected.estimatedDuration ? String(selected.estimatedDuration) : '', mandatory: selected.isMandatory ?? true })}>
                                    <EditOutlined fontSize="small" />
                                </IconButton>
                            </Box>
                            {selected.type !== 'blocks' ? (
                                // #526: the KI-Generator imports free-text questions as a classic lesson; the block editor would show it empty.
                                <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate(`/lesson/${selected.id}/content`)}>{translate('courseBuilder.classicLesson.open')}</Button>}>
                                    {translate('courseBuilder.classicLesson.hint')}
                                </Alert>
                            ) : (
                                <BlockLessonEditor lesson={selected} onSaved={onLessonSaved} />
                            )}
                        </>
                    ) : (
                        <Box className="oa-cb-empty">
                            <Typography variant="h6">{translate('courseBuilder.empty.title')}</Typography>
                            <Typography variant="body2" color="text.secondary">{translate('courseBuilder.empty.hint')}</Typography>
                        </Box>
                    )}
                </main>
            </Box>

            {/* lesson context menu */}
            <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
                <MenuItem onClick={() => { const l = menu!.lesson; setMenu(null); setLessonDialog({ open: true, lesson: l, sectionId: refId(l.section), title: l.title, duration: l.estimatedDuration ? String(l.estimatedDuration) : '', mandatory: l.isMandatory ?? true }); }}>
                    <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
                    <ListItemText>{translate('courseBuilder.lesson.edit')}</ListItemText>
                </MenuItem>
                <MenuItem disabled={sections.length < 2} onClick={(e) => { const l = menu!.lesson; setMenu(null); setMoveMenu({ anchor: e.currentTarget as HTMLElement, lesson: l }); }}>
                    <ListItemIcon><DriveFileMoveOutlined fontSize="small" /></ListItemIcon>
                    <ListItemText>{translate('courseBuilder.lesson.move')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { const l = menu!.lesson; setMenu(null); deleteLesson(l); }}>
                    <ListItemIcon><DeleteOutline fontSize="small" /></ListItemIcon>
                    <ListItemText>{translate('ra.action.delete')}</ListItemText>
                </MenuItem>
            </Menu>
            <Menu open={!!moveMenu} anchorEl={moveMenu?.anchor} onClose={() => setMoveMenu(null)}>
                {sections.filter((s) => s.id !== refId(moveMenu?.lesson.section ?? null)).map((s) => (
                    <MenuItem key={s.id} onClick={() => moveLesson(moveMenu!.lesson, s)}>{s.title}</MenuItem>
                ))}
            </Menu>

            {/* section dialog */}
            <Dialog open={sectionDialog.open} onClose={() => setSectionDialog((d) => ({ ...d, open: false }))} fullWidth maxWidth="xs">
                <DialogTitle>{sectionDialog.section ? translate('courseBuilder.section.rename') : translate('courseBuilder.section.add')}</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth margin="dense" label={translate('courseBuilder.section.titleField')} value={sectionDialog.title} onChange={(e) => setSectionDialog((d) => ({ ...d, title: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && saveSection()} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSectionDialog((d) => ({ ...d, open: false }))}>{translate('ra.action.cancel')}</Button>
                    <Button variant="contained" onClick={saveSection} disabled={busy}>{translate('ra.action.save')}</Button>
                </DialogActions>
            </Dialog>

            {/* lesson dialog */}
            <Dialog open={lessonDialog.open} onClose={() => setLessonDialog((d) => ({ ...d, open: false }))} fullWidth maxWidth="xs">
                <DialogTitle>{lessonDialog.lesson ? translate('courseBuilder.lesson.edit') : translate('courseBuilder.lesson.add')}</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth margin="dense" label={translate('courseBuilder.lesson.titleField')} value={lessonDialog.title} onChange={(e) => setLessonDialog((d) => ({ ...d, title: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && saveLesson()} />
                    <TextField fullWidth margin="dense" type="number" label={translate('courseBuilder.lesson.duration')} value={lessonDialog.duration} onChange={(e) => setLessonDialog((d) => ({ ...d, duration: e.target.value }))} inputProps={{ min: 0 }} />
                    <FormControlLabel control={<Switch checked={lessonDialog.mandatory} onChange={(e) => setLessonDialog((d) => ({ ...d, mandatory: e.target.checked }))} />} label={translate('lessons.mandatory')} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLessonDialog((d) => ({ ...d, open: false }))}>{translate('ra.action.cancel')}</Button>
                    <Button variant="contained" onClick={saveLesson} disabled={busy}>{translate('ra.action.save')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CourseBuilder;
